import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import {
  exhaustMap,
  EMPTY,
  Subject,
  catchError,
  map,
  switchMap,
  takeUntil,
  takeWhile,
  timer,
} from 'rxjs';

import { Flock, ImpactRow } from '../../../models/flock.model';
import {
  SustainabilityBackendService,
  FootprintCalculation,
  FootprintHistoryItem,
  FootprintIssue,
  FootprintReadiness,
  footprintStatus,
} from '../../../services/sustainability-backend.service';
import * as flockActions from '../../../store/actions/flock.actions';
import {
  getFlocks,
  selectSustainabilityNoticeVisibility,
} from '../../../store/selectors/flock.selectors';

@Component({
  selector: 'app-carbon-footprint',
  templateUrl: './carbon-footprint.component.html',
  styleUrls: ['./carbon-footprint.component.scss'],
  standalone: false,
})
export class CarbonFootprintComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private readonly selectionChanged$ = new Subject<void>();
  private readonly readinessRequests = new Subject<number>();
  private readonly resultRequests = new Subject<{ flockId: number; poll: boolean }>();
  // Browser polling is bounded; the persisted backend job continues independently.
  private readonly resultPollingDurationMs = 10 * 60 * 1000;
  readonly flocks$ = this.store
    .select(getFlocks)
    .pipe(map((flocks) => flocks.filter((flock) => !!flock.departure?.departureDate)));
  selectedFlock: Flock = null;
  showNotification = true;
  readonly readiness = signal<FootprintReadiness | null>(null);
  readonly checkingReadiness = signal(false);
  readonly readinessError = signal(false);
  readonly loadingResults = signal(false);
  readonly resultsError = signal(false);
  readonly calculating = signal(false);
  readonly calculationError = signal<string | null>(null);
  readonly status = signal('missing');
  readonly calculations = signal<FootprintHistoryItem[]>([]);
  readonly selectedCalculationId = signal<number | null>(null);
  readonly selectedCalculation = computed(() =>
    this.calculations().find((x) => x.id === this.selectedCalculationId()),
  );
  readonly canCalculate = computed(
    () =>
      !this.checkingReadiness() &&
      !this.calculating() &&
      !['pending', 'inprogress'].includes(this.status()) &&
      this.readiness()?.canCalculate === true &&
      ['flock', 'farm-resources', 'bedding', 'feeds'].every((requirement) =>
        this.readiness()?.checks.some(
          (check) => check.requirement === requirement && check.complete,
        ),
      ),
  );
  readonly readinessChecks = computed(() =>
    [...(this.readiness()?.checks ?? [])]
      .sort((a, b) => Number(a.complete) - Number(b.complete))
      .map((check) => ({ ...check, issueGroups: this.groupIssues(check.issues) })),
  );
  readonly completedRequirements = computed(
    () => this.readinessChecks().filter((check) => check.complete).length,
  );
  tableConfig: TableConfig = { dataSource: new MatTableDataSource<ImpactRow>([]), columns: [] };
  carbonFootprintErrors: string[] = [];
  private selectLatestResult = false;
  sustellLogoSrc = 'assets/images/sustell_logo.png';

  constructor(
    private readonly store: Store<RootState>,
    private readonly locale: LocaleService,
    private readonly dialogService: DialogService,
    public readonly service: SustainabilityBackendService,
  ) {}

  ngOnInit(): void {
    this.tableConfig = {
      ...this.tableConfig,
      pagination: false,
      selectColumn: false,
      actionColumn: false,
      scrolling: true,
      disableCards: true,
      wrapperWidth: '100%',
      actions: [],
      columns: [
        {
          id: 'name',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('sustainability.workflow.source'),
          disableFormatting: true,
        },
        {
          id: 'Consumption egg',
          type: TableColumnTypes.NUMBER,
          label: `${this.locale.translate('sustainability.workflow.outputs.eggs')} (${this.locale.translate('sustainability.workflow.outputs.egg-unit')})`,
          digitsInfo: '1.03-03',
        },
        {
          id: 'Spent laying hen',
          type: TableColumnTypes.NUMBER,
          label: `${this.locale.translate('sustainability.workflow.outputs.hens')} (${this.locale.translate('sustainability.workflow.outputs.hen-unit')})`,
          digitsInfo: '1.03-03',
        },
      ],
    };
    this.store
      .select(selectSustainabilityNoticeVisibility)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => (this.showNotification = value));
    this.readinessRequests
      .pipe(
        switchMap((flockId) => {
          this.readiness.set(null);
          this.checkingReadiness.set(true);
          this.readinessError.set(false);
          return this.service.readiness(flockId).pipe(
            takeUntil(this.selectionChanged$),
            catchError(() => {
              this.checkingReadiness.set(false);
              this.readinessError.set(true);
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((readiness) => {
        this.readiness.set(readiness);
        this.checkingReadiness.set(false);
      });
    this.resultRequests
      .pipe(
        switchMap(({ flockId, poll }) =>
          timer(0, 10000).pipe(
            exhaustMap(() => this.service.results(flockId)),
            takeWhile(
              (response) =>
                poll && ['pending', 'inprogress'].includes(footprintStatus(response.status)),
              true,
            ),
            takeUntil(timer(this.resultPollingDurationMs)),
            takeUntil(this.selectionChanged$),
            catchError(() => {
              this.loadingResults.set(false);
              this.resultsError.set(true);
              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe((response) => this.applyResults(response));
  }
  onFlockSelected(flock: Flock): void {
    this.selectionChanged$.next();
    this.selectedFlock = flock;
    this.selectLatestResult = false;
    this.readiness.set(null);
    this.checkingReadiness.set(false);
    this.readinessError.set(false);
    this.calculating.set(false);
    this.calculationError.set(null);
    this.status.set('missing');
    this.calculations.set([]);
    this.selectCalculation(null);
    this.store.dispatch(
      flockActions.setSelectedFlockId({ id: flock.id, save: true, reloadWeekData: false }),
    );
    this.checkReadiness();
    this.loadResults();
  }
  checkReadiness(): void {
    if (this.selectedFlock) this.readinessRequests.next(this.selectedFlock.id);
  }
  loadResults(): void {
    if (!this.selectedFlock) return;
    this.loadingResults.set(true);
    this.resultsError.set(false);
    this.resultRequests.next({ flockId: this.selectedFlock.id, poll: true });
  }
  private applyResults(response: FootprintCalculation): void {
    if (response.flockId !== this.selectedFlock?.id) return;
    const previousIds = new Set(this.calculations().map((item) => item.id));
    this.calculations.set(response.results ?? []);
    this.status.set(footprintStatus(response.status));
    this.loadingResults.set(false);
    const fresh = this.calculations().find(
      (item) => !previousIds.has(item.id) && footprintStatus(item.status) === 'available',
    );
    const selection = this.selectLatestResult
      ? this.calculations()[0]
      : (fresh ??
        this.calculations().find((item) => item.id === this.selectedCalculationId()) ??
        this.calculations()[0]);
    this.selectLatestResult = false;
    this.selectCalculation(selection?.id ?? null);
  }
  startCalculateCarbonFootprint(): void {
    if (!this.selectedFlock || !this.canCalculate()) return;
    const flockId = this.selectedFlock.id;
    this.dialogService
      .confirmWarning({
        title: this.locale.translate('sustainability.carbon-footprint-title'),
        text: this.locale.translate('sustainability.carbon-footprint-text'),
      })
      .pipe(takeUntil(this.selectionChanged$), takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (
          result.status !== DIALOG_RETURN_TYPES.confirm ||
          !this.canCalculate() ||
          this.selectedFlock?.id !== flockId
        )
          return;
        this.calculating.set(true);
        this.calculationError.set(null);
        this.service
          .calculate(flockId)
          .pipe(takeUntil(this.selectionChanged$), takeUntil(this.destroyed$))
          .subscribe({
            next: (response) => {
              this.calculating.set(false);
              if (footprintStatus(response.status) === 'failed') {
                this.calculationError.set(response.errorCode ?? 'sustell-submission-failed');
                if (response.readiness) this.readiness.set(response.readiness);
                else this.checkReadiness();
                if (response.errorCode !== 'calculation-not-ready') {
                  this.selectLatestResult = true;
                  this.loadResults();
                }
                return;
              }
              this.status.set(footprintStatus(response.status));
              this.selectLatestResult = true;
              this.loadResults();
            },
            error: () => {
              this.calculating.set(false);
              this.calculationError.set('sustell-submission-failed');
              this.checkReadiness();
            },
          });
      });
  }
  refreshCarbonFootprintResult(): void {
    if (!this.selectedFlock || this.calculating() || this.loadingResults()) return;
    this.loadingResults.set(true);
    this.resultsError.set(false);
    this.service
      .refresh(this.selectedFlock.id)
      .pipe(takeUntil(this.selectionChanged$), takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.errorCode) {
            this.loadingResults.set(false);
            this.resultsError.set(true);
            return;
          }
          this.loadResults();
        },
        error: () => {
          this.loadingResults.set(false);
          this.resultsError.set(true);
        },
      });
  }
  selectCalculation(id: number | null): void {
    this.selectedCalculationId.set(id);
    this.carbonFootprintErrors = [];
    const rows: Record<string, ImpactRow> = {};
    try {
      const parsed = this.selectedCalculation()?.resultJson
        ? JSON.parse(this.selectedCalculation().resultJson)
        : {};
      const footprint = parsed.footprint ?? parsed.Footprint ?? {};
      const stages: {
        animalType?: string;
        AnimalType?: string;
        AnimalTypeValue?: string;
        impacts?: { name: string; value: number }[];
        Impacts?: { Name: string; Value: number }[];
      }[] = footprint.stagesImpact ?? footprint.StagesImpact ?? [];
      for (const stage of stages)
        for (const impact of stage.impacts ??
          stage.Impacts?.map((impact) => ({ name: impact.Name, value: impact.Value })) ??
          []) {
          const name = impact.name;
          const animal = stage.animalType ?? stage.AnimalType ?? stage.AnimalTypeValue ?? '';
          rows[name] ??= { name };
          rows[name][animal] = impact.value;
        }
      this.carbonFootprintErrors = (footprint.errors ?? footprint.Errors ?? []).flatMap(
        (error: { messages?: string[]; Messages?: string[] }) =>
          error.messages ?? error.Messages ?? [],
      );
    } catch {
      this.carbonFootprintErrors = [
        this.locale.translate('sustainability.workflow.result-invalid'),
      ];
    }
    this.tableConfig = {
      ...this.tableConfig,
      dataSource: new MatTableDataSource(
        Object.values(rows)
          .filter((row) => row.name.toLowerCase() !== 'climate change')
          .map((row) => ({ ...row, name: row.name.replace(/^climate change\s*-\s*/i, '') })),
      ),
    };
  }
  calculationStatus(item: FootprintHistoryItem): string {
    return footprintStatus(item.status);
  }

  climateImpacts(
    item: FootprintHistoryItem,
  ): { animalType: string; value: number; unit: string }[] {
    if (this.calculationStatus(item) !== 'available') return [];
    try {
      const parsed = JSON.parse(item.resultJson);
      const footprint = parsed.footprint ?? parsed.Footprint ?? {};
      interface StoredImpact {
        name?: string;
        Name?: string;
        value?: number;
        Value?: number;
        unit?: string;
        Unit?: string;
      }
      const stages: {
        animalType?: string;
        AnimalType?: string;
        AnimalTypeValue?: string;
        impacts?: StoredImpact[];
        Impacts?: StoredImpact[];
      }[] = footprint.stagesImpact ?? footprint.StagesImpact ?? [];
      return stages.flatMap((stage) =>
        (stage.impacts ?? stage.Impacts ?? [])
          .filter((impact) => (impact.name ?? impact.Name)?.toLowerCase() === 'climate change')
          .flatMap((impact) => {
            const value = impact.value ?? impact.Value;
            return typeof value === 'number'
              ? [
                  {
                    animalType: stage.animalType ?? stage.AnimalType ?? stage.AnimalTypeValue ?? '',
                    value,
                    unit: impact.unit ?? impact.Unit ?? 'kg CO2 eq',
                  },
                ]
              : [];
          }),
      );
    } catch {
      return [];
    }
  }

  private groupIssues(issues: FootprintIssue[]) {
    const groups = new Map<string, FootprintIssue[]>();
    for (const issue of issues) {
      const group = groups.get(issue.code) ?? [];
      group.push(issue);
      groups.set(issue.code, group);
    }
    return [...groups.values()].map((group) => ({
      issues: group,
      details: group.filter((issue) => issue.deliveryLineId || issue.year || issue.fields?.length),
    }));
  }

  issueFields(issue: FootprintIssue): string {
    return (issue.fields ?? [])
      .map((field) => {
        const key = `sustainability.workflow.fields.${field}`;
        const translated = this.locale.translate(key);
        return translated === key ? field.replace(/([a-z])([A-Z])/g, '$1 $2') : translated;
      })
      .join(', ');
  }

  issueText(issue: FootprintIssue): string {
    return this.locale.translate(`sustainability.workflow.issues.${issue.code}`);
  }
  getActionText(): string {
    return this.locale.translate(
      this.showNotification
        ? 'sustainability.action-text-close'
        : 'sustainability.action-text-open',
    );
  }
  toggleNotification(): void {
    this.store.dispatch(
      flockActions.setSustainabilityNoticeVisibility({ visible: !this.showNotification }),
    );
  }
  ngOnDestroy(): void {
    this.selectionChanged$.next();
    this.selectionChanged$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
